import React, { use, useCallback, useMemo } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client/react";
import { GET_MISSION_VACATION_STATUS, GET_PAYROLL_BREAKDOWN } from "../../graphql/timesheet";
import PayrollBreakdownHourly from "../../components/editors/payroll-breakdown/PayrollBreakdownHourly";
import PayrollBreakdownMonthly from "../../components/editors/payroll-breakdown/PayrollBreakdownMonthly";
import ScreenHeader from "./ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import { usePayrollBreakdownFormatter } from "../../hooks/payroll-beakdown-utils";
import ScreenCard from "../ScreenCard";
import Loading from "../../components/Loading";
import Gap from "../../ui_atoms/Gap";
import { RootStackParamList } from "../../navigations/RootNavigation";
import VStack from "../../ui_atoms/VStack";
import { PayrollBreakdownStatus, ProfileType, TimeSheetState } from "../../__generated__/graphql";
import { isLiftTimesheetSource } from "../../utils";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import PayrollBreakdownSummary, {
    attachGrossAmountsToSummaryRows,
} from "../client/PayrollBreakdownSummary";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import PayrollBreakdownInfo from "../../components/editors/payroll-breakdown/PayrollBreakdownInfo";
import PayrollBreakdownAdditions from "../../components/editors/payroll-breakdown/PayrollBreakdownAdditions";
import { FULL_DATE_FORMAT, useDateFormatter } from "../../hooks/date";
import { useScrollHeaderColorSync } from "../../hooks/use-scroll-header-color-sync";
import { useTheme } from "../../hooks/use-theme";

const VIEWERS = {
    hourly: PayrollBreakdownHourly,
    daily: PayrollBreakdownHourly,
    monthly: PayrollBreakdownMonthly,
    package: PayrollBreakdownHourly,
    undefined: null,
};

type PayrollBreakdownDetailsScreenProps = StackScreenProps<
    RootStackParamList,
    "PayrollBreakdownDetails"
>;

const PayrollBreakdownDetailsScreen = ({
    route,
    navigation,
}: PayrollBreakdownDetailsScreenProps) => {
    const payrollBreakdownId = route.params.payrollBreakdownId;
    const i18n = useI18n();
    const { colors } = useTheme();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { data, error, loading, refetch } = useQuery(GET_PAYROLL_BREAKDOWN, {
        variables: { payrollBreakdownId },
        skip: !payrollBreakdownId,
    });
    const payrollBreakdown = data?.getPayrollBreakdown;

    const {
        data: vacationStatusData,
        error: vacationStatusError,
        loading: vacationStatusLoading,
        refetch: _vacationStatusRefetch,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown,
        fetchPolicy: "cache-and-network",
    });
    const vacationStatusRefetch = payrollBreakdown ? _vacationStatusRefetch : () => {};
    const vacationStatus = vacationStatusData?.getMissionVacationStatus;

    const { screenLabel, screenSubLabel } = usePayrollBreakdownFormatter();
    const formatDate = useDateFormatter();
    const onBack = useCallback(() => {
        navigation?.goBack();
    }, [navigation]);
    const Viewer = VIEWERS[payrollBreakdown?.missionInfo?.earningType || "undefined"];

    const allCompleted = payrollBreakdown?.timesheets?.every(
        (ts) => ts.status === TimeSheetState.Submitted || ts.status === TimeSheetState.Accepted,
    );
    const hasOpenTimesheet = payrollBreakdown?.timesheets?.some(
        (ts) => ts.status === TimeSheetState.Open,
    );
    const lastRejectedTimeSheet = payrollBreakdown?.timesheets?.find(
        (ts) => ts.status === TimeSheetState.Rejected,
    );
    const descriptionStatus = useMemo(() => {
        // a paid décompte has been submitted before
        if (payrollBreakdown?.status && payrollBreakdown.status !== PayrollBreakdownStatus.Open) {
            if (payrollBreakdown?.submittedBy) {
                return "submitted";
            }
            return "auto_submitted";
        } else {
            if (!isLiftTimesheetSource(payrollBreakdown?.missionInfo.payrollSource)) {
                return "external_ts";
            }
            if (allCompleted) {
                return "lift_ts_completed";
            }
            if (hasOpenTimesheet || lastRejectedTimeSheet) {
                return "lift_ts_open";
            }
        }
        return "";
    }, [payrollBreakdown, allCompleted, hasOpenTimesheet, lastRejectedTimeSheet]);

    const { onScroll, headerBackgroundColor } = useScrollHeaderColorSync({
        aboveLimitColor: colors.accentLighter,
        belowLimitColor: "white",
    });

    return (
        <>
            <ScreenHeader
                label={screenLabel(payrollBreakdown)}
                subLabel={screenSubLabel(payrollBreakdown)}
                onLeftAction={onBack}
                useSmallLabel
                backgroundColor={headerBackgroundColor}
            />
            <ScreenCard
                refresh={() => Promise.all([refetch(), vacationStatusRefetch()])}
                headband={
                    error || vacationStatusError
                        ? buildErrorHeadband(i18n.t("screens.payroll_breakdown.cannot_load"))
                        : undefined
                }
                onScroll={onScroll}
            >
                {!payrollBreakdown && (loading || vacationStatusLoading) && <Loading />}

                {profileType && payrollBreakdown && vacationStatus && Viewer && (
                    <VStack>
                        <PayrollBreakdownInfo
                            payrollBreakdown={payrollBreakdown}
                            introSentence={i18n.t(
                                `screens.payroll_breakdown.info_text.${profileType}.${descriptionStatus}`,
                                {
                                    workerName: payrollBreakdown.missionInfo?.workerInfo?.name,
                                    clientName: payrollBreakdown.submittedBy
                                        ? `${payrollBreakdown.submittedBy.firstName} ${payrollBreakdown.submittedBy.lastName}`
                                        : payrollBreakdown.missionInfo?.clientInfo?.profileName,
                                    companyName: payrollBreakdown.missionInfo?.clientInfo?.name,
                                    date: payrollBreakdown.submittedAt
                                        ? formatDate(
                                              new Date(payrollBreakdown.submittedAt),
                                              FULL_DATE_FORMAT,
                                          )
                                        : "",
                                },
                            )}
                        />
                        <Viewer
                            payrollBreakdown={payrollBreakdown}
                            vacationStatus={vacationStatus}
                            profileType={profileType}
                            navigation={navigation}
                        />
                        {/* what the employer entered, once the décompte is transmitted */}
                        {payrollBreakdown.status !== PayrollBreakdownStatus.Open && (
                            <>
                                <Gap gap={1} />
                                <PayrollBreakdownAdditions payrollBreakdown={payrollBreakdown} />
                            </>
                        )}
                        {profileType === ProfileType.Client && (
                            <>
                                <Gap gap={1} />
                                <PayrollBreakdownSummary
                                    rows={attachGrossAmountsToSummaryRows(
                                        payrollBreakdown.summary.rows || [],
                                        payrollBreakdown,
                                    )}
                                    needProcessing={payrollBreakdown.summary.needProcessing}
                                    payrollBreakdownStatus={payrollBreakdown.status}
                                    loading={loading}
                                    error={error}
                                />
                            </>
                        )}
                    </VStack>
                )}
                <Gap gap={1} />
            </ScreenCard>
        </>
    );
};

export default PayrollBreakdownDetailsScreen;
